import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { useNotify } from 'react-admin';
import CodeIcon from '@mui/icons-material/Code';
import auth from '../../../auth/auth0';
import { useApiConfig } from '../../../services/apiConfig';
import LogsModal from './LogsModal.component';
import { leadImportStatuses } from '../../leadImports.model';
import logger from '../../../utils/logger';
/**
 * Button component to view processing logs for a lead import job
 */
const ViewLogsButton = ({
  record,
  openImmediately = false,
  onClose: propOnClose,
}) => {
  const [open, setOpen] = useState(openImmediately);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState(null);
  const notify = useNotify();
  const apiConfig = useApiConfig();

  // Effect to handle programmatic opening
  useEffect(() => {
    if (openImmediately) {
      handleOpen();
    }
  }, [openImmediately]);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiConfig.getApiUrl('leadImports')}/lead-imports/${record.id}/logs`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.getIdToken()}`,
          },
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No processing logs available for this import job.');
        }
        throw new Error(`Failed to fetch logs: ${response.statusText}`);
      }

      const data = await response.json();
      setLogs(data.logs);
    } catch (error) {
      logger.error('Error fetching logs:', error);
      setError(error.message);
      notify(error.message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLogs(null);
    setError(null);

    // Call the parent's onClose handler if provided
    if (propOnClose) {
      propOnClose();
    }
  };

  // Don't show the button if the job is still in QUEUED status or if it's meant to be opened programmatically
  if (record.processingStatus === leadImportStatuses.QUEUED || openImmediately) {
    return (
      <LogsModal
        open={open}
        onClose={handleClose}
        loading={loading}
        logs={logs}
        error={error}
        filename={record.filename}
      />
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CodeIcon />}
        onClick={handleOpen}
        size="small"
      >
        View Processing Logs
      </Button>

      <LogsModal
        open={open}
        onClose={handleClose}
        loading={loading}
        logs={logs}
        error={error}
        filename={record.filename}
      />
    </>
  );
};

export default ViewLogsButton;
